<script lang="ts">
	import { fly, slide } from 'svelte/transition';
	import { cubicOut } from 'svelte/easing';
	import { toast } from './toast.svelte.ts';
	import Fa from 'svelte-fa';
	import {
		faCheckCircle,
		faExclamationTriangle,
		faInfoCircle,
		faBan
	} from '@fortawesome/free-solid-svg-icons';

	// Subscribe directly to the toast store
	let toasts = $state([]);

	$effect(() => {
		const unsubscribe = toast.subscribe(value => {
			toasts = value;
		});
		return unsubscribe;
	});

	function getIcon(type: string) {
		switch (type) {
			case 'success': return faCheckCircle;
			case 'error': return faBan;
			case 'warning': return faExclamationTriangle;
			case 'ghost': return faInfoCircle;
			case 'info':
			default: return faInfoCircle;
		}
	}

	function getPosition(toast: any) {
		return toast.position || 'bottom-right';
	}

	function getTransition(position: string, slidePreference: string = 'horizontal') {
		const isRight = position.includes('right');
		const isLeft = position.includes('left');
		const isBottom = position.includes('bottom');
		const isTop = position.includes('top');

		let x = 0;
		let y = 0;

		if (slidePreference === 'horizontal') {
			// Only slide horizontally
			x = isRight ? 400 : isLeft ? -400 : 0;
			// For center positions, still need to pick a direction
			if (position.includes('center')) {
				x = 400; // Default slide from right for center positions
			}
		} else if (slidePreference === 'vertical') {
			// Only slide vertically
			y = isBottom ? 100 : isTop ? -100 : 0;
			// For center positions, this works naturally
		}

		return {
			x,
			y,
			duration: 400,
			easing: cubicOut
		};
	}
</script>

<!-- Single container for all toasts -->
<div class="toasts-wrapper">
	{#each toasts as t (t.id)}
		{@const position = getPosition(t)}
		{@const slidePreference = t.slidePreference || 'horizontal'}
		<div class="toast-container {position}">
			<div
				class="toast toast-{t.type}"
				in:fly={getTransition(position, slidePreference)}
				out:fly={{ ...getTransition(position, slidePreference), duration: 250, easing: cubicOut }}
				role="alert"
			>
				{#if t.showIcon !== false}
					<span class="toast-icon">
						<Fa icon={getIcon(t.type)} size="1x" />
					</span>
				{/if}
				<span class="toast-message" class:centered={t.centerText}>{t.message}</span>
				{#if t.showClose !== false}
					<button class="toast-close" onclick={() => toast.remove(t.id)}>×</button>
				{/if}
			</div>
		</div>
	{/each}
</div>

<style>
	.toasts-wrapper {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		pointer-events: none;
		z-index: 9999;
	}

	.toast-container {
		position: fixed;
		pointer-events: none;
		padding: 1rem;
	}

	.toast-container.top-left {
		top: 0;
		left: 0;
	}

	.toast-container.top-center {
		top: 0;
		left: 50%;
		transform: translateX(-50%);
	}

	.toast-container.top-right {
		top: 0;
		right: 0;
	}

	.toast-container.bottom-left {
		bottom: 0;
		left: 0;
	}

	.toast-container.bottom-center {
		bottom: 0;
		left: 50%;
		transform: translateX(-50%);
	}

	.toast-container.bottom-right {
		bottom: 0;
		right: 0;
	}

	.toast {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		padding: 0.75rem 1rem;
		background: white;
		border-radius: 0.375rem;
		box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
		pointer-events: auto;
		min-width: 250px;
		max-width: 400px;
		border-left: 4px solid;
		border-right: 4px solid;
		transform-origin: center;
		will-change: transform, opacity;
	}

	.toast-success {
		border-left-color: #48c774;
		border-right-color: #48c774;
		background: #effaf3;
		color: #257942;
	}

	.toast-error {
		border-left-color: #ff3860;
		border-right-color: #ff3860;
		background: #feecf0;
		color: #cc0f35;
	}

	.toast-warning {
		border-left-color: #ffdd57;
		border-right-color: #ffdd57;
		background: #fffbeb;
		color: #947600;
	}

	.toast-info {
		border-left-color: #ff3e00;
		border-right-color: #ff3e00;
		background: #fff5f2;
		color: #ff3e00;
	}

	.toast-ghost {
		border: none;
		background: transparent;
		box-shadow: none;
		color: #ff3e00;
		min-width: auto;
		max-width: none;
		width: auto;
		padding: 0.25rem 0.5rem;
	}

	.toast-icon {
		flex-shrink: 0;
		display: flex;
		align-items: center;
		font-size: 1rem;
	}

	.toast-message {
		flex: 1;
		font-size: 0.875rem;
		line-height: 1.25rem;
	}

	.toast-message.centered {
		text-align: center;
	}

	.toast-close {
		background: none;
		border: none;
		color: currentColor;
		opacity: 0.6;
		cursor: pointer;
		font-size: 1.25rem;
		line-height: 1;
		padding: 0;
		width: 1.25rem;
		height: 1.25rem;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.toast-close:hover {
		opacity: 1;
	}
</style>